import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  Treemap,
  ScatterChart,
  Scatter,
  ReferenceLine,
  ReferenceArea,
  ComposedChart,
  Line,
  AreaChart,
  Area,
  LineChart,
} from '../lib/semanticChartLibrary'

import {
  createWaterfallData,
  formatCellValue,
  formatMetric,
  getChartColors,
  humanize,
  isNumberLike,
  normalizeChartData,
  validateSemanticChartConfig,
} from '../lib/queryWorkspaceUtils'

const CHART_HEIGHT = 300
const CARTESIAN_MARGIN = { top: 12, right: 24, left: 18, bottom: 44 }
const GRID_COLOR = '#e5e7eb'
const AXIS_TICK_COLOR = '#475569'
const SERIES_STROKE = '#ffffff'

function MeasuredChart({ children, height = CHART_HEIGHT }) {
  const ref = useRef(null)
  const [width, setWidth] = useState(720)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const updateWidth = () => {
      const rect = node.getBoundingClientRect()
      const nextWidth = Math.floor(rect.width || node.clientWidth || 720)
      setWidth(Math.max(nextWidth, 320))
    }

    updateWidth()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateWidth) : null
    observer?.observe(node)
    window.addEventListener('resize', updateWidth)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  return (
    <div ref={ref} className="semantic-chart-measure" style={{ height }}>
      {width > 0 ? children({ width, height }) : null}
    </div>
  )
}

function shortAxisLabel(value, max = 16) {
  const text = String(value ?? '')
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

function formatAxisNumber(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  if (Math.abs(numeric) >= 10000000) return `${formatMetric(numeric / 10000000)} Cr`
  if (Math.abs(numeric) >= 100000) return `${formatMetric(numeric / 100000)} L`
  return formatMetric(numeric)
}

export function VisualizationSwitcher({ data, response, config, onPageChange }) {
  const normalized = useMemo(() => normalizeChartData(data, config), [data, config])
  const validation = useMemo(() => validateSemanticChartConfig(data, config), [data, config])

  if (config.viewType !== 'table' && !validation.ok) {
    return <SemanticChartEmptyState message={validation.message} response={response} config={config} onPageChange={onPageChange} />
  }

  switch (config.viewType) {
    case 'table':
      return <DataTableView response={response} config={config} onPageChange={onPageChange} />
    case 'bar':
      return <BarView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'line':
      return <LineView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'area':
      return <AreaView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'combo':
      return <ComboView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'stackedBar':
      return <StackedBarView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'pie':
    case 'donut':
    case 'rose':
    case 'donutRose':
      return <PieFamilyView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'quadrant':
      return <QuadrantView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'treemap':
      return <TreemapView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    case 'waterfall':
      return <WaterfallView rows={normalized} config={config} response={response} onPageChange={onPageChange} />
    default:
      return <DataTableView response={response} config={config} onPageChange={onPageChange} />
  }
}

function hasNextPage(response) {
  const data = response?.data || {}
  const page = data.page || 1
  const pageSize = data.page_size || 25
  const totalPages = data.total_pages || 1
  const rowCount = data.row_count ?? data.rows?.length ?? 0
  return page < totalPages || rowCount >= pageSize
}

function ResultPagination({ response, onPageChange }) {
  if (!response?.data || !onPageChange) return null
  const page = response.data.page || 1
  const pageSize = response.data.page_size || 25
  const totalPages = response.data.total_pages || 1
  const nextEnabled = hasNextPage(response)
  return (
    <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
      <div className="text-xs text-slate-500">
        Page {page} of {Math.max(totalPages, page)} · showing {response.data.rows?.length || response.data.row_count || 0} records
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(Math.max(page - 1, 1))}
          disabled={page <= 1}
          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!nextEnabled}
          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next {nextEnabled ? pageSize : ''}
        </button>
      </div>
    </div>
  )
}

function ChartShell({ title, subtitle, children, footer, pagination }) {
  return (
    <div className="semantic-chart-surface flex h-full min-h-[300px] w-full min-w-0 flex-col">
      <div className="mb-3 flex items-start justify-between gap-3 px-1">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Visualization</div>
          <div className="mt-1 text-sm font-semibold text-slate-900 md:text-base">{title}</div>
          {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
        </div>
      </div>
      <div className="semantic-chart-body min-h-[300px] w-full min-w-0 flex-1">{children}</div>
      {pagination}
      {footer ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          {footer}
        </div>
      ) : null}
    </div>
  )
}

function SemanticChartEmptyState({ message, response, config, onPageChange }) {
  return (
    <ChartShell
      title="Chart mapping needs attention"
      subtitle={message || 'The selected chart cannot be rendered from the returned columns.'}
      footer="Adjust the chart fields from Customize, or use the table view to inspect the raw query result."
    >
      <div className="flex h-[210px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
        <div>
          <div className="text-sm font-semibold text-slate-800">Showing table instead</div>
          <div className="mt-1 text-xs text-slate-500">This prevents the semantic workspace from drawing a chart with incorrect axes or zero-filled values.</div>
        </div>
      </div>
      <div className="mt-4">
        <DataTableView response={response} config={{ ...config, viewType: 'table' }} onPageChange={onPageChange} />
      </div>
    </ChartShell>
  )
}

function DataTableView({ response, config, onPageChange }) {
  const columns = useMemo(() => response?.data?.columns || [], [response])
  const rows = useMemo(() => response?.data?.rows || [], [response])
  const [sort, setSort] = useState({ field: columns[0] || '', direction: 'asc' })

  useEffect(() => {
    if (columns.length && !columns.includes(sort.field)) {
      setSort({ field: columns[0], direction: 'asc' })
    }
  }, [columns, sort.field])

  const sortedRows = useMemo(() => {
    if (!sort.field) return rows
    const cloned = [...rows]
    cloned.sort((a, b) => {
      const left = a[sort.field]
      const right = b[sort.field]
      if (isNumberLike(left) && isNumberLike(right)) {
        return sort.direction === 'asc' ? Number(left) - Number(right) : Number(right) - Number(left)
      }
      return sort.direction === 'asc'
        ? String(left ?? '').localeCompare(String(right ?? ''))
        : String(right ?? '').localeCompare(String(left ?? ''))
    })
    return cloned
  }, [rows, sort])

  const totals = useMemo(() => {
    const numeric = columns.filter((column) => rows.some((row) => isNumberLike(row[column])))
    const acc = {}
    numeric.forEach((column) => {
      acc[column] = rows.reduce((sum, row) => sum + (Number(row[column]) || 0), 0)
    })
    return acc
  }, [columns, rows])

  return (
    <ChartShell
      title={config.viewType === 'table' ? 'Table view' : `${humanize(config.valueField)} by ${humanize(config.categoryField)}`}
      subtitle={`${response?.data?.full_row_count || 0} total rows · page ${response?.data?.page || 1} of ${response?.data?.total_pages || 1}`}
      footer="Use the sort controls in the table headers and page controls below to browse larger result sets."
    >
      <div className="h-[260px] overflow-auto rounded-2xl border border-slate-200">
        <table className="min-w-full border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr className="text-xs text-slate-700">
              {columns.map((column) => (
                <th key={column} className="border-b border-slate-200 px-3 py-2.5 font-semibold">
                  <button
                    onClick={() =>
                      setSort((current) => ({
                        field: column,
                        direction: current.field === column && current.direction === 'asc' ? 'desc' : 'asc',
                      }))
                    }
                    className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-slate-100"
                  >
                    <span>{humanize(column)}</span>
                    {sort.field === column ? <span>{sort.direction === 'asc' ? '↑' : '↓'}</span> : null}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={`${index}-${columns[0] || 'row'}`} className="text-sm odd:bg-white even:bg-slate-50/60">
                {columns.map((column) => (
                  <td key={column} className="border-b border-slate-100 px-3 py-2.5 text-slate-900">
                    {formatCellValue(row[column], column, response?.data?.column_formats || {}, response?.data?.column_types || {}, config.salesUnit)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {Object.keys(totals).length ? (
            <tfoot className="bg-slate-50">
              <tr>
                {columns.map((column, index) => (
                  <td key={column} className="px-3 py-2.5 text-slate-900">
                    {index === 0
                      ? <span className="font-semibold text-slate-700">Total</span>
                      : column in totals
                        ? formatCellValue(totals[column], column, response?.data?.column_formats || {}, response?.data?.column_types || {}, config.salesUnit)
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          Showing {response?.data?.rows?.length || 0} rows on this page
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max((response?.data?.page || 1) - 1, 1))}
            disabled={(response?.data?.page || 1) <= 1}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange((response?.data?.page || 1) + 1)}
            disabled={!hasNextPage(response)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </ChartShell>
  )
}

function BarView({ rows, config, response, onPageChange }) {
  const isHorizontal = config.orientation === 'horizontal'
  const colors = getChartColors(config, rows.length)
  return (
    <ChartShell title={`${humanize(config.valueField)} by ${humanize(config.categoryField)}`} subtitle="Bar chart view" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <BarChart width={width} height={height} data={rows} layout={isHorizontal ? 'vertical' : 'horizontal'} margin={CARTESIAN_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            isHorizontal ? (
              <>
                <XAxis type="number" tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
                <YAxis type="category" dataKey="name" width={118} tickFormatter={(value) => shortAxisLabel(value, 18)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              </>
            ) : (
              <>
                <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
                <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              </>
            )
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={config.barSize}>
            {rows.map((row, index) => (
              <Cell key={row.id} fill={colors[index % colors.length]} stroke={SERIES_STROKE} strokeWidth={1} />
            ))}
          </Bar>
        </BarChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function LineView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, 3)
  return (
    <ChartShell title={`${humanize(config.lineField)} trend by ${humanize(config.categoryField)}`} subtitle="Line chart view" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <LineChart width={width} height={height} data={rows} margin={CARTESIAN_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            <>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
            </>
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Line type="monotone" dataKey="line" name={humanize(config.lineField)} stroke={colors[0]} strokeWidth={3.5} dot={{ r: 3.5, fill: colors[0], stroke: SERIES_STROKE, strokeWidth: 1.5 }} activeDot={{ r: 5.5, stroke: SERIES_STROKE, strokeWidth: 2 }} />
        </LineChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function AreaView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, 3)
  return (
    <ChartShell title={`${humanize(config.valueField)} area view`} subtitle="Area chart view" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <AreaChart width={width} height={height} data={rows} margin={CARTESIAN_MARGIN}>
          <defs>
            <linearGradient id="valueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colors[0]} stopOpacity={0.55} />
              <stop offset="95%" stopColor={colors[0]} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            <>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
            </>
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Area type="monotone" dataKey="value" name={humanize(config.valueField)} stroke={colors[0]} fill="url(#valueFill)" strokeWidth={3} />
        </AreaChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function ComboView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, 4)
  return (
    <ChartShell title={`${humanize(config.valueField)} + ${humanize(config.lineField)}`} subtitle="Bar and line combined" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <ComposedChart width={width} height={height} data={rows} margin={CARTESIAN_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            <>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
            </>
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Bar dataKey="value" name={humanize(config.valueField)} barSize={config.barSize} radius={[8, 8, 0, 0]} fill={colors[0]} stroke={SERIES_STROKE} strokeWidth={1} />
          <Line type="monotone" dataKey="line" name={humanize(config.lineField)} stroke={colors[2]} strokeWidth={3.5} dot={{ r: 3.5, fill: colors[2], stroke: SERIES_STROKE, strokeWidth: 1.5 }} />
        </ComposedChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function StackedBarView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, 4)
  return (
    <ChartShell title={`${humanize(config.valueField)} + ${humanize(config.compareField)}`} subtitle="Stacked bar view" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <BarChart width={width} height={height} data={rows} margin={CARTESIAN_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            <>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
            </>
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Bar dataKey="value" name={humanize(config.valueField)} stackId="a" fill={colors[0]} stroke={SERIES_STROKE} strokeWidth={0.75} radius={[6, 6, 0, 0]} />
          <Bar dataKey="compare" name={humanize(config.compareField)} stackId="a" fill={colors[1]} stroke={SERIES_STROKE} strokeWidth={0.75} radius={[6, 6, 0, 0]} />
        </BarChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function PieFamilyView({ rows, config, response, onPageChange }) {
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  const maxValue = Math.max(...rows.map((row) => row.value), 1)
  const colors = getChartColors(config, rows.length)
  const legendLayout = config.orientation === 'horizontal' ? 'horizontal' : 'vertical'
  const legendAlign = config.orientation === 'horizontal' ? 'center' : 'right'
  const legendVerticalAlign = config.orientation === 'horizontal' ? 'bottom' : 'middle'

  const getOuterRadius = (entry) => {
    if (config.viewType === 'pie' || config.viewType === 'donut') return config.outerRadius
    const minRadius = config.viewType === 'donutRose' ? Math.max(config.innerRadius + 18, config.outerRadius * 0.56) : Math.max(34, config.outerRadius * 0.42)
    return minRadius + (entry.value / maxValue) * (config.outerRadius - minRadius)
  }

  const innerRadius = config.viewType === 'donut' || config.viewType === 'donutRose' ? config.innerRadius : 0

  const labelRenderer = ({ name, percent, cx, cy, midAngle, outerRadius }) => {
    if (!config.showLabels) return null
    const angle = Math.PI / 180
    const r = outerRadius + config.labelOffset
    const x = cx + r * Math.cos(-midAngle * angle)
    const y = cy + r * Math.sin(-midAngle * angle)
    return (
      <text x={x} y={y} fill="#111827" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10} fontWeight={500}>
        {`${name} ${(percent * 100).toFixed(1)}%`}
      </text>
    )
  }

  return (
    <ChartShell title={`${config.viewType} chart`} subtitle={`Total ${humanize(config.valueField)}: ${formatMetric(total)}`} pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <PieChart width={width} height={height}>
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          {config.showLegend ? <Legend layout={legendLayout} align={legendAlign} verticalAlign={legendVerticalAlign} wrapperStyle={{ fontSize: 11 }} /> : null}
          <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={innerRadius} outerRadius={(entry) => getOuterRadius(entry)} paddingAngle={config.paddingAngle} labelLine={config.showLabels} label={config.showLabels ? labelRenderer : false} stroke="#ffffff" strokeWidth={2}>
            {rows.map((row, index) => (
              <Cell key={row.id} fill={colors[index % colors.length]} />
            ))}
          </Pie>
        </PieChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function QuadrantView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, 4)
  const xValues = rows.map((row) => row.x)
  const yValues = rows.map((row) => row.y)
  const avgX = rows.reduce((sum, row) => sum + row.x, 0) / Math.max(rows.length, 1)
  const avgY = rows.reduce((sum, row) => sum + row.y, 0) / Math.max(rows.length, 1)
  const maxSize = Math.max(...rows.map((row) => row.size), 1)
  const xMin = Math.min(...xValues, 0)
  const xMax = Math.max(...xValues, 1)
  const yMin = Math.min(...yValues, 0)
  const yMax = Math.max(...yValues, 1)
  const xPad = Math.max(1, (xMax - xMin) * 0.18 || 1)
  const yPad = Math.max(1, (yMax - yMin) * 0.18 || 1)

  const scatterData = rows.map((row) => {
    const quadrant =
      row.x >= avgX && row.y >= avgY
        ? { label: 'Leaders', fill: colors[0] }
        : row.x < avgX && row.y >= avgY
          ? { label: 'Potential', fill: colors[1] }
          : row.x < avgX && row.y < avgY
            ? { label: 'Watchlist', fill: colors[2] }
            : { label: 'High X / Low Y', fill: colors[3] }

    return {
      ...row,
      z: 150 + (row.size / maxSize) * 780,
      quadrantLabel: quadrant.label,
      bubbleFill: quadrant.fill,
    }
  })

  return (
    <ChartShell title={`Quadrant: ${humanize(config.xField)} vs ${humanize(config.yField)}`} subtitle={`Bubble size mapped to ${humanize(config.sizeField)}`} pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <ScatterChart width={width} height={height} margin={CARTESIAN_MARGIN}>
          <ReferenceArea x1={xMin - xPad} x2={avgX} y1={avgY} y2={yMax + yPad} fill="#fef3c7" fillOpacity={0.35} />
          <ReferenceArea x1={avgX} x2={xMax + xPad} y1={avgY} y2={yMax + yPad} fill="#dcfce7" fillOpacity={0.35} />
          <ReferenceArea x1={xMin - xPad} x2={avgX} y1={yMin - yPad} y2={avgY} fill="#ede9fe" fillOpacity={0.35} />
          <ReferenceArea x1={avgX} x2={xMax + xPad} y1={yMin - yPad} y2={avgY} fill="#e0f2fe" fillOpacity={0.35} />
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="4 4" />
          {config.showAxis ? (
            <>
              <XAxis type="number" dataKey="x" name={humanize(config.xField)} domain={[xMin - xPad, xMax + xPad]} tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis type="number" dataKey="y" name={humanize(config.yField)} domain={[yMin - yPad, yMax + yPad]} tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <ZAxis type="number" dataKey="size" range={[150, 900]} />
            </>
          ) : null}
          <ReferenceLine x={avgX} stroke="#334155" strokeDasharray="6 6" label={{ value: `Avg ${humanize(config.xField)}`, position: 'insideTop', fill: '#334155', fontSize: 10 }} />
          <ReferenceLine y={avgY} stroke="#334155" strokeDasharray="6 6" label={{ value: `Avg ${humanize(config.yField)}`, position: 'insideRight', fill: '#334155', fontSize: 10 }} />
          {config.showTooltip ? <Tooltip content={<QuadrantTooltip config={config} />} cursor={{ strokeDasharray: '4 4' }} /> : null}
          <Scatter data={scatterData} shape={<QuadrantBubble showLabels={config.showLabels} />} />
        </ScatterChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function QuadrantBubble(props) {
  const { cx, cy, size, payload, showLabels } = props
  const radius = Math.max(9, Math.sqrt(size || 220) / 2.35)
  return (
    <g>
      <circle cx={cx} cy={cy} r={radius + 3} fill={payload.bubbleFill} fillOpacity={0.12} />
      <circle cx={cx} cy={cy} r={radius} fill={payload.bubbleFill} fillOpacity={0.88} stroke="#ffffff" strokeWidth={3} />
      <circle cx={cx - radius * 0.28} cy={cy - radius * 0.28} r={radius * 0.24} fill="#ffffff" fillOpacity={0.35} />
      {showLabels ? <text x={cx} y={cy + radius + 12} textAnchor="middle" fontSize={10} fontWeight={600} fill="#0f172a">{payload.name}</text> : null}
    </g>
  )
}

function QuadrantTooltip({ active, payload, config }) {
  if (!active || !payload?.length) return null
  const point = payload[0].payload
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="mb-1 text-sm font-semibold text-slate-900">{point.name}</div>
      <div className="mb-2 inline-flex rounded-full px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: point.bubbleFill }}>
        {point.quadrantLabel}
      </div>
      <div className="space-y-1 text-xs text-slate-600">
        <div>{humanize(config.xField)}: <span className="font-semibold text-slate-900">{point.x}</span></div>
        <div>{humanize(config.yField)}: <span className="font-semibold text-slate-900">{point.y}</span></div>
        <div>{humanize(config.sizeField)}: <span className="font-semibold text-slate-900">{formatMetric(point.size)}</span></div>
      </div>
    </div>
  )
}

function TreemapView({ rows, config, response, onPageChange }) {
  const colors = getChartColors(config, rows.length)
  return (
    <ChartShell title={`Tree chart by ${humanize(config.categoryField)}`} subtitle={`Area is based on ${humanize(config.valueField)}`} pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <Treemap width={width} height={height} data={rows.map((row, index) => ({ name: row.name, size: row.value, fill: colors[index % colors.length] }))} dataKey="size" stroke="#fff" fill="#8884d8" content={<CustomTreemapContent showLabels={config.showLabels} />} />
        )}
      </MeasuredChart>
    </ChartShell>
  )
}

function CustomTreemapContent(props) {
  const { root, depth, x, y, width, height, index, name, showLabels } = props
  if (!root) return null
  const fill = root.children?.[index]?.fill || '#3b82f6'
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} style={{ fill, stroke: '#fff', strokeWidth: 2 }} />
      {showLabels && width > 70 && height > 24 ? <text x={x + 8} y={y + 18} fill="#fff" fontSize={11} fontWeight={600}>{name}</text> : null}
      {depth === 1 && showLabels && width > 70 && height > 40 ? <text x={x + 8} y={y + 34} fill="#fff" fontSize={10}>{formatMetric(root.children[index]?.size || 0)}</text> : null}
    </g>
  )
}

function WaterfallView({ rows, config, response, onPageChange }) {
  const waterfallRows = createWaterfallData(rows)
  const colors = getChartColors(config, waterfallRows.length)
  return (
    <ChartShell title={`Waterfall: cumulative ${humanize(config.valueField)}`} subtitle="Progressive running total by category" pagination={<ResultPagination response={response} onPageChange={onPageChange} />}>
      <MeasuredChart>
        {({ width, height }) => (
        <BarChart width={width} height={height} data={waterfallRows} margin={CARTESIAN_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
          {config.showAxis ? (
            <>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={64} interval={0} tickFormatter={(value) => shortAxisLabel(value)} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
              <YAxis tickFormatter={formatAxisNumber} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }} />
            </>
          ) : null}
          {config.showTooltip ? <Tooltip content={<StandardTooltip title={humanize(config.categoryField)} />} /> : null}
          <Bar dataKey="invisible" stackId="wf" fill="transparent" />
          <Bar dataKey="visible" stackId="wf" radius={[8, 8, 0, 0]}>
            {waterfallRows.map((row, index) => (
              <Cell key={row.name} fill={colors[index % colors.length]} stroke={SERIES_STROKE} strokeWidth={1} />
            ))}
          </Bar>
        </BarChart>
        )}
      </MeasuredChart>
    </ChartShell>
  )
}


function StandardTooltip({ active, payload, label, title }) {
  if (!active || !payload?.length) return null
  return (
    <div className="min-w-[170px] rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-xl">
      <div className="mb-2 border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{title || label}</div>
      <div className="space-y-1.5">
        {payload.map((entry, index) => (
          <div key={`${entry.name}-${index}`} className="flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-slate-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color || entry.payload?.bubbleFill || '#2563eb' }} />
              <span>{entry.name}</span>
            </div>
            <span className="font-semibold text-slate-900">{typeof entry.value === 'number' ? formatCellValue(entry.value, entry.name) : entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
